import React from 'react';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import { SkeletonItem } from '@helpcenter/skeleton-item';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useExternalLinkIcon } from 'view/external-links/use-external-link-icon';
import type { Size, IconSize } from '@atlaskit/icon/types';
import BookIcon from '@atlaskit/icon/core/book-with-bookmark';
import QuestionsIcon from '@atlaskit/icon/core/comment';
import HipchatLobbyIcon from '@atlaskit/icon/core/lobby-bell';
import { token } from '@atlaskit/tokens';
import messages from './messages';
import { iconDimension } from './styled';
import type { iconTypes, Props } from './types';

const convertSizeToIconSize = (size: Size): IconSize => {
	return size === 'small' ? 'small' : 'medium';
};

const getFallbackIcon = ({
	iconType,
	intl,
	size,
}: {
	iconType: iconTypes;
	intl: IntlShape;
	size: Size;
}) => {
	const icon = token('color.icon', '#42526E');
	switch (iconType) {
		case 'REQUEST_FORM': {
			return (
				<HipchatLobbyIcon
					label={intl.formatMessage(messages.externalResourceRequestFormIconLabel)}
					size={convertSizeToIconSize(size)}
					color={icon}
				/>
			);
		}
		case 'ARTICLE': {
			return (
				<BookIcon
					label={intl.formatMessage(messages.externalResourceArticleIconLabel)}
					size={convertSizeToIconSize(size)}
					color={icon}
					spacing="spacious"
				/>
			);
		}
		case 'CHANNEL':
		default: {
			return (
				<QuestionsIcon
					label={intl.formatMessage(messages.externalResourceHelpChannelIconLabel)}
					size={convertSizeToIconSize(size)}
					color={icon}
				/>
			);
		}
	}
};

const getIcon = ({
	isIconLoading,
	iconUrl,
	resourceType,
	intl,
	size = 'large',
}: {
	isIconLoading: boolean;
	iconUrl: string;
	resourceType: iconTypes;
	intl: IntlShape;
	size?: Size;
}) => {
	if (isIconLoading) {
		return <SkeletonItem height={iconDimension} width={iconDimension} />;
	}
	if (iconUrl) {
		return <AutoScaledImage src={iconUrl} inheritWidth draggable={false} />;
	}
	return getFallbackIcon({ intl, size, iconType: resourceType });
};

const ExternalResourceIcon: React.FC<Props> = (props: Props) => {
	di(useExternalLinkIcon);

	const { data, isLoading } = useExternalLinkIcon({
		isLoggedIn: props.isUserLoggedIn,
		link: props.iconUrl || '',
	});

	return getIcon({
		isIconLoading: isLoading,
		iconUrl: data || '',
		resourceType: props.resourceType,
		intl: props.intl,
		size: props.size,
	});
};

export default injectIntl(ExternalResourceIcon);
